import { supabase } from "@/integrations/supabase/client";
import type {
	SyncResult,
	Transcript,
	TranscriptEntityType,
	TranscriptListItem,
	UploadTranscriptPayload,
} from "@/types/transcript";

// Thin typed fetchers around the `microsoft-transcript-fetch` edge-function
// commands, so react-query owns caching and retries and components never
// query the transcript tables directly.

class TranscriptApiError extends Error {}

function assertOk(error: unknown, data: unknown): void {
	if (error)
		throw error instanceof Error ? error : new TranscriptApiError(String(error));
	const dataError = (data as { error?: string } | null)?.error;
	if (dataError)
		throw new TranscriptApiError(dataError);
}

export async function listTranscripts(): Promise<TranscriptListItem[]> {
	const { data, error } = await supabase.functions.invoke(
		"microsoft-transcript-fetch?command=fetch-all",
		{ method: "GET" }
	);
	assertOk(error, data);
	return Array.isArray(data) ? data : [];
}

export async function getTranscript(transcriptId: string): Promise<Transcript> {
	const { data, error } = await supabase.functions.invoke(
		`microsoft-transcript-fetch?command=fetch-one&transcript_id=${transcriptId}`,
		{ method: "GET" }
	);
	assertOk(error, data);
	return data as Transcript;
}

export async function listTranscriptsForCandidate(candidateId: string): Promise<TranscriptListItem[]> {
	const { data, error } = await supabase.functions.invoke(
		`microsoft-transcript-fetch?command=fetch-for-candidate&candidate_id=${candidateId}`,
		{ method: "GET" }
	);
	assertOk(error, data);
	return Array.isArray(data) ? data : [];
}

export async function syncTranscripts(): Promise<SyncResult> {
	const { data, error } = await supabase.functions.invoke(
		"microsoft-transcript-fetch?command=fetch-transcripts",
		{ method: "POST" }
	);
	assertOk(error, data);
	return {
		fetched: data?.fetched ?? 0,
		linked: data?.linked ?? 0,
		eventsScanned: data?.eventsScanned ?? 0,
	};
}

export async function linkCandidate(transcriptId: string, candidateId: string): Promise<void> {
	const { data, error } = await supabase.functions.invoke(
		"microsoft-transcript-fetch?command=link-candidate",
		{ method: "POST", body: { transcript_id: transcriptId, candidate_id: candidateId } }
	);
	assertOk(error, data);
}

export async function unlinkCandidate(transcriptId: string, candidateId: string): Promise<void> {
	const { data, error } = await supabase.functions.invoke(
		"microsoft-transcript-fetch?command=unlink-candidate",
		{ method: "POST", body: { transcript_id: transcriptId, candidate_id: candidateId } }
	);
	assertOk(error, data);
}

export async function linkEntity(
	transcriptId: string,
	entityType: TranscriptEntityType,
	entityId: string
): Promise<void> {
	const { data, error } = await supabase.functions.invoke(
		"microsoft-transcript-fetch?command=link-entity",
		{ method: "POST", body: { transcript_id: transcriptId, entity_type: entityType, entity_id: entityId } }
	);
	assertOk(error, data);
}

export async function unlinkEntity(
	transcriptId: string,
	entityType: TranscriptEntityType,
	entityId: string
): Promise<void> {
	const { data, error } = await supabase.functions.invoke(
		"microsoft-transcript-fetch?command=unlink-entity",
		{ method: "POST", body: { transcript_id: transcriptId, entity_type: entityType, entity_id: entityId } }
	);
	assertOk(error, data);
}

export async function generateSummary(transcriptId: string): Promise<string> {
	const { data, error } = await supabase.functions.invoke(
		"microsoft-transcript-fetch?command=generate-summary",
		{ method: "POST", body: { transcript_id: transcriptId } }
	);
	assertOk(error, data);
	return data?.summary ?? "";
}

export async function generateInsights(transcriptId: string): Promise<Transcript["insights"]> {
	const { data, error } = await supabase.functions.invoke(
		"microsoft-transcript-fetch?command=generate-insights",
		{ method: "POST", body: { transcript_id: transcriptId } }
	);
	assertOk(error, data);
	return data?.insights ?? null;
}

export async function uploadTranscript(payload: UploadTranscriptPayload): Promise<{ id: string }> {
	const { data, error } = await supabase.functions.invoke(
		"microsoft-transcript-fetch?command=upload-transcript",
		{ method: "POST", body: payload }
	);
	assertOk(error, data);
	return { id: data?.id };
}

export async function deleteTranscript(transcriptId: string): Promise<void> {
	const { data, error } = await supabase.functions.invoke(
		"microsoft-transcript-fetch?command=delete-transcript",
		{ method: "POST", body: { transcript_id: transcriptId } }
	);
	assertOk(error, data);
}
